import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Globe, RefreshCcw, AlertTriangle, XCircle, Mail, HelpCircle, ShieldX, Building2 } from 'lucide-react';

const RefundPolicyPage = () => {
  const lastUpdated = "December 13, 2024";
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-700/50 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center">
              <Globe className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white">innovatorfoundervisas.com</span>
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-red-500/10 text-red-400 px-4 py-2 rounded-full text-sm mb-6">
            <RefreshCcw className="w-4 h-4" />
            Refund Policy
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Refund Policy
          </h1>
          <p className="text-slate-400">
            Last updated: {lastUpdated}
          </p>
        </div>

        {/* Important Notice Banner */}
        <div className="bg-red-500/20 border-2 border-red-500/50 rounded-2xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-red-500/30 rounded-xl flex items-center justify-center flex-shrink-0">
              <ShieldX className="w-6 h-6 text-red-400" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white mb-2">No Refunds After Portal Access</h3>
              <p className="text-red-200">
                Once you have completed a purchase and logged into the portal, all sales are final and non-refundable. Please review our services carefully before purchasing.
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 p-8 md:p-12 space-y-8">
          
          {/* Company Information */}
          <section className="bg-slate-700/30 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <Building2 className="w-6 h-6 text-red-500" />
              Company Information
            </h2>
            <div className="text-slate-300 space-y-4">
              <p>
                <strong className="text-white">innovatorfoundervisas.com</strong> is a product of <strong className="text-white">Cloud Lab Technologies - UK</strong>, a UK-based IT Consulting Firm owned by <strong className="text-white">Ahmad Raza</strong>.
              </p>
              <p className="text-slate-400 text-sm">
                Ahmad Raza is himself a UK Innovator Founder Visa holder and serves as a mentor on this platform, sharing his first-hand experience with aspiring entrepreneurs.
              </p>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div className="bg-slate-800/50 p-4 rounded-lg">
                  <p className="text-slate-400 text-sm">Company</p>
                  <p className="text-white font-semibold">Cloud Lab Technologies - UK</p>
                </div>
                <div className="bg-slate-800/50 p-4 rounded-lg">
                  <p className="text-slate-400 text-sm">Owner & Mentor</p>
                  <p className="text-white font-semibold">Ahmad Raza (UK Innovator Founder Visa Holder)</p>
                </div>
              </div>
            </div>
          </section>

          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <RefreshCcw className="w-6 h-6 text-red-500" />
              1. Overview
            </h2>
            <div className="text-slate-300 space-y-4">
              <p>
                Cloud Lab Technologies - UK, operating innovatorfoundervisas.com, provides digital products and services including AI-powered tools, educational content, and visa preparation resources. Due to the nature of our digital products and immediate access to our platform, we maintain a strict refund policy.
              </p>
              <p>
                <strong className="text-white">By completing a purchase on our platform, you acknowledge and agree to this refund policy.</strong>
              </p>
            </div>
          </section>

          {/* Core Policy */}
          <section className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <XCircle className="w-6 h-6 text-red-500" />
              2. No Refund Policy
            </h2>
            <div className="text-slate-300 space-y-4">
              <p className="text-lg font-semibold text-red-400">
                ALL SALES ARE FINAL. NO REFUNDS WILL BE PROVIDED ONCE YOU HAVE:
              </p>
              <ul className="list-disc list-inside space-y-3 ml-4">
                <li><strong className="text-white">Completed a purchase</strong> of any package, credits, or service</li>
                <li><strong className="text-white">Logged into the portal</strong> using your registered account</li>
                <li><strong className="text-white">Accessed any content</strong> including AI tools, courses, templates, or resources</li>
                <li><strong className="text-white">Used any AI credits</strong> or platform features</li>
              </ul>
              <p className="mt-4 text-slate-400">
                This policy applies to all products and services offered by Cloud Lab Technologies - UK through innovatorfoundervisas.com, including but not limited to: Core packages, Premium packages, AI credit purchases, interview credits, Zoom consultation bookings, and White Glove services.
              </p>
            </div>
          </section>

          {/* Why No Refunds */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <HelpCircle className="w-6 h-6 text-red-500" />
              3. Why We Have This Policy
            </h2>
            <div className="text-slate-300 space-y-4">
              <p>Cloud Lab Technologies - UK maintains this no-refund policy because:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong className="text-white">Immediate Digital Access:</strong> Upon purchase, you receive instant access to our platform, AI tools, and content. Digital products cannot be &ldquo;returned&rdquo; once accessed.</li>
                <li><strong className="text-white">AI Resource Consumption:</strong> Our AI-powered tools consume computational resources the moment they are used. These costs are incurred immediately upon access.</li>
                <li><strong className="text-white">Proprietary Content:</strong> Our business plan templates, interview preparation materials, and educational content are proprietary. Once viewed, this information cannot be &ldquo;unlearned.&rdquo;</li>
                <li><strong className="text-white">Service Delivery:</strong> We deliver our services immediately upon purchase completion and portal access.</li>
              </ul>
            </div>
          </section>

          {/* Important Disclaimer */}
          <section className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-500" />
              4. Important Disclaimer
            </h2>
            <div className="text-slate-300 space-y-4">
              <p className="font-semibold text-amber-400">
                PLEASE READ BEFORE PURCHASING:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Cloud Lab Technologies - UK is a UK-based IT Consulting Firm, <strong className="text-white">NOT</strong> a UK Home Office endorsed body</li>
                <li>We are <strong className="text-white">NOT</strong> an immigration law firm or regulated immigration adviser</li>
                <li>We do <strong className="text-white">NOT</strong> guarantee visa approval or endorsement</li>
                <li>Our services are <strong className="text-white">educational and preparatory tools only</strong></li>
                <li>Ahmad Raza&apos;s mentorship is based on personal experience, <strong className="text-white">NOT</strong> legal advice</li>
                <li>Visa outcomes depend on factors <strong className="text-white">outside our control</strong></li>
              </ul>
              <p className="mt-4 text-amber-300">
                Refunds will NOT be issued based on visa application outcomes, endorsement decisions, or any results from UK Home Office or endorsing bodies. You are purchasing preparation tools and educational content, not guaranteed outcomes.
              </p>
            </div>
          </section>

          {/* Exceptions */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5. Limited Exceptions</h2>
            <div className="text-slate-300 space-y-4">
              <p>Refunds may ONLY be considered in the following exceptional circumstances:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong className="text-white">Duplicate Charges:</strong> If you were charged multiple times for the same purchase due to a technical error</li>
                <li><strong className="text-white">Payment Processing Error:</strong> If you were charged but never received access to the platform (must be reported within 24 hours)</li>
                <li><strong className="text-white">Pre-Access Cancellation:</strong> If you contact us to cancel BEFORE logging into the portal for the first time (within 24 hours of purchase)</li>
              </ul>
              <p className="mt-4 text-slate-400">
                All exception requests are subject to review and verification by Cloud Lab Technologies - UK. We reserve the right to deny any refund request that does not meet these criteria.
              </p>
            </div>
          </section>

          {/* Before You Purchase */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. Before You Purchase</h2>
            <div className="text-slate-300 space-y-4">
              <p>We strongly encourage you to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Review all information on our website thoroughly</li>
                <li>Watch any available demo videos or previews</li>
                <li>Read our Terms & Conditions and this Refund Policy completely</li>
                <li>Contact our support team with any questions BEFORE purchasing</li>
                <li>Ensure you understand what is included in each package</li>
                <li>Confirm this service is appropriate for your visa situation</li>
              </ul>
              <p className="mt-4">
                Our support team is available at <strong className="text-white">support@innovatorfoundervisas.com</strong> to answer any pre-purchase questions.
              </p>
            </div>
          </section>

          {/* Chargebacks */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7. Chargebacks</h2>
            <div className="text-slate-300 space-y-4">
              <p>
                By purchasing our services, you agree not to initiate a chargeback or dispute with your bank or payment provider after accessing our platform.
              </p>
              <p>
                If you initiate a chargeback after accessing the portal:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Your account will be <strong className="text-white">immediately and permanently suspended</strong></li>
                <li>You may be <strong className="text-white">banned from future purchases</strong></li>
                <li>Cloud Lab Technologies - UK reserves the right to <strong className="text-white">pursue legal action</strong> to recover losses</li>
                <li>We will provide evidence to your bank showing you accessed the services</li>
              </ul>
            </div>
          </section>

          {/* White Glove Services */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8. White Glove / Done-For-You Services</h2>
            <div className="text-slate-300 space-y-4">
              <p>
                For our premium White Glove services provided by Cloud Lab Technologies - UK:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>All payments are <strong className="text-white">non-refundable</strong> once the service engagement begins</li>
                <li>Service engagement begins upon <strong className="text-white">first consultation or onboarding call</strong></li>
                <li>Partial refunds are <strong className="text-white">not available</strong> for incomplete work</li>
                <li>If you choose to discontinue, remaining work will not be completed and no refund will be issued</li>
              </ul>
            </div>
          </section>

          {/* Policy Changes */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">9. Policy Changes</h2>
            <div className="text-slate-300 space-y-4">
              <p>
                Cloud Lab Technologies - UK reserves the right to modify this Refund Policy at any time. Changes will be effective immediately upon posting to this page. The &ldquo;Last updated&rdquo; date at the top indicates when the policy was last revised.
              </p>
              <p>
                Purchases made before a policy change will be governed by the policy in effect at the time of purchase.
              </p>
            </div>
          </section>

          {/* Contact */}
          <section className="bg-slate-700/30 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <Mail className="w-6 h-6 text-red-500" />
              10. Contact Us
            </h2>
            <div className="text-slate-300 space-y-4">
              <p>For questions about this policy or to report a billing issue:</p>
              <ul className="space-y-2">
                <li><strong className="text-white">Company:</strong> Cloud Lab Technologies - UK</li>
                <li><strong className="text-white">Owner:</strong> Ahmad Raza (UK Innovator Founder Visa Holder & Platform Mentor)</li>
                <li><strong className="text-white">Billing Issues:</strong> billing@innovatorfoundervisas.com</li>
                <li><strong className="text-white">General Support:</strong> support@innovatorfoundervisas.com</li>
                <li><strong className="text-white">Website:</strong> innovatorfoundervisas.com</li>
              </ul>
              <p className="mt-4 text-sm text-slate-400">
                For duplicate charge issues, please include your transaction ID and email us within 24 hours of the charge.
              </p>
            </div>
          </section>

        </div>

        {/* Final Notice */}
        <div className="mt-8 bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 text-center">
          <p className="text-slate-400 text-sm">
            By completing a purchase on innovatorfoundervisas.com (a product of Cloud Lab Technologies - UK), you confirm that you have read, understood, and agree to this Refund Policy. If you do not agree with this policy, please do not make a purchase.
          </p>
        </div>

        {/* Related Links */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Link 
            to="/terms" 
            className="px-6 py-3 bg-slate-700/50 hover:bg-slate-700 text-white rounded-lg transition-colors"
          >
            Terms & Conditions
          </Link>
          <Link 
            to="/privacy" 
            className="px-6 py-3 bg-slate-700/50 hover:bg-slate-700 text-white rounded-lg transition-colors"
          >
            Privacy Policy & GDPR
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-700/50 mt-12 py-8">
        <div className="max-w-5xl mx-auto px-4 text-center text-slate-500 text-sm">
          <p>© {new Date().getFullYear()} Cloud Lab Technologies - UK. All rights reserved.</p>
          <p className="mt-1">innovatorfoundervisas.com is a product of Cloud Lab Technologies - UK</p>
        </div>
      </footer>
    </div>
  );
};

export default RefundPolicyPage;
